import type { MatterContactConnection } from "@/lib/types";
import { getMatterContactConnections } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { getIncidentTypeLabel } from "@/lib/utils/matters/incident-classification";
import { retrieveClaimTypesQueryOptions } from "@/lib/data/react-query/matters/claim-type";
import type { DirectoryUser } from "@/lib/types";
import { createContactConnectionChildInput } from "@/lib/utils/connections/item-contact-connections";
import { MATTER_PARTY_CONNECTION_TYPES } from "@/lib/utils/connections/matter-party-connections";
import {
  type LeadAsideFormValues,
  leadAsideFormSchema,
} from "@/components/features/leads/leadAsideFormSchema";
import { LeadAssignPopover } from "@/components/features/leads/LeadAssignPopover";
import { LeadConversionModal } from "@/components/features/leads/LeadConversionModal";
import { LeadsAssigneeSummary } from "@/components/features/leads/LeadsAssigneeSummary";
import { PossibleDuplicateContactModal } from "@/components/features/contact/PossibleDuplicateContactModal";
import { type MatterStatusGroup } from "@/components/features/leads/LeadStatusTransitionDropdown";
import {
  MultiselectFormField,
  AppModal,
  Badge,
  Button,
  DateFormField,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Form,
  Label,
  IncidentTypeBadge,
  Select,
  SelectContent,
  SelectFormField,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SimpleSelectFormField,
  StateSelectFormField,
  SwitchFormField,
  TextAreaFormField,
  TextFormField,
  Textarea,
  Typography,
} from "@/components/ui";
import { ActionConfirmationModal } from "@/components/ui/ActionConfirmationModal/ActionConfirmationModal";
import { MultiCategorySelectFormField } from "@/components/ui/FormFields/MultiCategorySelectFormField/MultiCategorySelectFormField";
import LawWorksLoader from "@/components/ui/LawWorksLoader/LawWorksLoader";
import { getIncidentTypeBadgeVariantFromId } from "@/components/ui/MatterTypeBadge/MatterTypeBadge";
import { useUser } from "@/components/UserProvider";
import {
  retrieveContactQueryOptions,
  useCreateContact,
  useUpdateContact,
} from "@/lib/data/react-query/contacts/contact";
import {
  useCreateContactEmail,
  useUpdateContactEmail,
} from "@/lib/data/react-query/contacts/contact-email";
import {
  useCreateContactPhone,
  useUpdateContactPhone,
} from "@/lib/data/react-query/contacts/contact-phone";
import { useUpdateUser } from "@/lib/data/react-query/firm/firm-user";
import {
  retrieveMatterQueryOptions,
  useCreateMatter,
  useDeleteMatter,
  useUpdateMatter,
} from "@/lib/data/react-query/matters/matter";
import { useCreateMatterAssignee } from "@/lib/data/react-query/matters/matter-assignee";
import {
  retrieveMatterNotesQueryOptions,
  useCreateMatterNote,
  useDeleteMatterNote,
  useUpdateMatterNote,
} from "@/lib/data/react-query/matters/matter-note";
import { useCreateMatterStatusPhase } from "@/lib/data/react-query/matters/matter-status";
import {
  useCreateMatterStatuteOfLimitations,
  useDeleteMatterStatuteOfLimitations,
  useUpdateMatterStatuteOfLimitations,
} from "@/lib/data/react-query/matters/matter-statute-of-limitations";
import { useCreateMatterTeamConnection } from "@/lib/data/react-query/matters/matter-team";
import { useCreateMatterTeammate } from "@/lib/data/react-query/matters/matter-teammate";
import {
  isPossibleDuplicateResponse,
  type PossibleDuplicateContact,
} from "@/lib/data/requests";
import { type MatterSearchResult } from "@/lib/data/requests/matters/search-matters";
import { queryKeys } from "@/lib/data/static";
import {
  CONTACT_INDIVIDUAL_TIMEZONES,
  LANGUAGES,
  formatContactIndividualTimezoneLabel,
} from "@/lib/data/static/contactStatics";
import { getPrimaryMatterStatute } from "@/lib/data/static/matters/statuteStatics";
import {
  MATTER_STATUS_CATEGORY_DISPLAY_LABELS,
  type MatterStatusCategoryOption,
} from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { resolveReportPrimaryStatute } from "@/lib/utils/matters/report-statute-utils";
import type {
  Contact,
  Matter,
  MatterNote,
  MatterRole,
  MatterStatus,
  MatterStatuteOfLimitations,
  MatterTeam,
  IncidentType,
  User,
} from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import {
  formatContactsName,
  formatDate,
  formatUsersName,
} from "@/lib/utils/format";
import {
  getMatterSearchResultAssigneeDisplayNames,
  getMatterSearchResultClientConnection,
  getMatterSearchResultClientName,
} from "@/lib/utils/matters/matter-search-result-utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { ArrowRight, ChevronDown, Settings2, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";

type IntakePipelineCategory = Extract<
  MatterStatusCategoryOption,
  "lead" | "opportunity"
>;

type LeadEditAsideProps = {
  isOpen: boolean;
  selectedLead?: MatterSearchResult;
  matterStatuses: MatterStatus[];
  matterStatusGroups: MatterStatusGroup[];
  conversionMatterStatusGroups: MatterStatusGroup[];
  conversionTarget: "lead" | "matter";
  matterRoles: MatterRole[];
  matterTeams: MatterTeam[];
  incidentTypes: IncidentType[];
  canTransitionMatterStatus: boolean;
  isMatterSettingsLoading: boolean;
  isMatterSettingsError: boolean;
  onClose: () => void;
  /**
   * Controls which intake pipeline statuses appear when creating a new record
   * and the default status category for the form.
   */
  intakePipelineCategory?: IntakePipelineCategory;
  /**
   * Refreshes the intake list after a composed action succeeds.
   */
  refreshLeadsListData: () => void;
};

const MATTER_NOTE_SUB_ITEM_FIELDS = [
  "vehicleId",
  "insuranceId",
  "medicalTreatmentId",
  "medicalTreatmentAppointmentId",
  "medicalTreatmentBillId",
  "medicalTreatmentRecordId",
  "medicalTreatmentPlanId",
  "medicalTreatmentBalanceConfirmationId",
  "settlementDemandId",
  "settlementOfferId",
  "settlementCounterId",
  "emailId",
  "expenseId",
  "lostWagesId",
  "lienId",
  "propertyDamageId",
  "signatureRequestId",
  "trustCheckId",
  "incidentReportId",
  "litigationDateId",
  "fileId",
] as const satisfies readonly (keyof MatterNote)[];

const getLeadIntakeMatterNote = (notes: MatterNote[]): MatterNote | undefined =>
  [...notes]
    .filter((note) =>
      MATTER_NOTE_SUB_ITEM_FIELDS.every((field) => !note[field]),
    )
    .sort((a, b) => dayjs(a.dateCreated).diff(dayjs(b.dateCreated)))[0];

const formatNoteMessageForForm = (message?: string | null) =>
  message?.replace(/\n+$/, "") ?? "";

type LeadEmail =
  | string
  | {
      address?: string | null;
      isPrimary?: boolean | null;
    };

type LeadPhone =
  | string
  | {
      number?: string | null;
      isPrimary?: boolean | null;
    };

const getLeadNameParts = (name?: string | null) => {
  const [firstName = "", ...lastNameParts] = (name ?? "").trim().split(/\s+/);

  return {
    firstName,
    lastName: lastNameParts.join(" "),
  };
};

const getPrimaryEmailAddress = (emails?: LeadEmail[] | null) => {
  const primaryEmail =
    emails?.find((email) => typeof email !== "string" && email.isPrimary) ??
    emails?.[0];

  if (!primaryEmail) {
    return "";
  }

  return typeof primaryEmail === "string"
    ? primaryEmail
    : (primaryEmail.address ?? "");
};

const getPrimaryPhoneNumber = (phones?: LeadPhone[] | null) => {
  const primaryPhone =
    phones?.find((phone) => typeof phone !== "string" && phone.isPrimary) ??
    phones?.[0];

  if (!primaryPhone) {
    return "";
  }

  return typeof primaryPhone === "string"
    ? primaryPhone
    : (primaryPhone.number ?? "");
};

const trimFormValue = (value: string) => value.trim();

const SILENT_COMPOSED_MUTATION_OPTIONS = {
  invalidateOnSettled: false,
  showSuccessToast: false,
} as const;

const formatFormDateValue = (value?: string | null) =>
  value ? dayjs(value).format("YYYY-MM-DD") : "";

const getMatterConnectionContactName = (connection?: MatterContactConnection) =>
  formatContactsName(connection?.contact) ?? "";

type MatterTeamConnectionTeammate = {
  userId?: string | null;
};

type MatterTeamConnectionV2 = Matter["teamConnections"][number] & {
  dateTimeEnd?: string | null;
  teammates?: MatterTeamConnectionTeammate[];
};

const syncLeadProjectedStatute = async ({
  matterId,
  projectedSolDate,
  existingStatute,
  createMatterStatute,
  updateMatterStatute,
  deleteMatterStatute,
}: {
  matterId: string;
  projectedSolDate: string;
  existingStatute?: MatterStatuteOfLimitations;
  createMatterStatute: ReturnType<typeof useCreateMatterStatuteOfLimitations>;
  updateMatterStatute: ReturnType<typeof useUpdateMatterStatuteOfLimitations>;
  deleteMatterStatute: ReturnType<typeof useDeleteMatterStatuteOfLimitations>;
}) => {
  const trimmedDate = trimFormValue(projectedSolDate);

  if (trimmedDate) {
    if (existingStatute) {
      const existingDate = formatFormDateValue(existingStatute.date);
      if (trimmedDate === existingDate) {
        return;
      }

      await updateMatterStatute.mutateAsync({
        matterId,
        id: existingStatute.id,
        dateModified: existingStatute.dateModified,
        date: trimmedDate,
      });
      return;
    }

    await createMatterStatute.mutateAsync({
      matterId,
      date: trimmedDate,
    });
    return;
  }

  if (existingStatute) {
    await deleteMatterStatute.mutateAsync({
      matterId,
      id: existingStatute.id,
    });
  }
};

const syncLeadMatterNote = async ({
  matterId,
  noteMessage,
  existingNote,
  currentUser,
  createMatterNote,
  updateMatterNote,
  deleteMatterNote,
}: {
  matterId: string;
  noteMessage: string;
  existingNote?: MatterNote;
  currentUser: User;
  createMatterNote: ReturnType<typeof useCreateMatterNote>;
  updateMatterNote: ReturnType<typeof useUpdateMatterNote>;
  deleteMatterNote: ReturnType<typeof useDeleteMatterNote>;
}) => {
  const trimmedMessage = trimFormValue(noteMessage);

  if (trimmedMessage) {
    if (existingNote) {
      const existingMessage = formatNoteMessageForForm(existingNote.message);
      if (trimmedMessage === existingMessage) {
        return;
      }

      await updateMatterNote.mutateAsync({
        matterId,
        id: existingNote.id,
        dateModified: existingNote.dateModified,
        message: trimmedMessage,
        draft: false,
      });
      return;
    }

    await createMatterNote.mutateAsync({
      matterId,
      message: trimmedMessage,
      draft: false,
      pinned: false,
      userId: currentUser.id,
    });
    return;
  }

  if (existingNote) {
    await deleteMatterNote.mutateAsync({
      matterId,
      id: existingNote.id,
    });
  }
};

const buildLeadMatterTitle = (
  data: LeadAsideFormValues,
  untitledFallback: string,
) => {
  const contactName = [data.firstName, data.lastName]
    .map(trimFormValue)
    .filter(Boolean)
    .join(" ");
  const dateOfLoss = data.matterDateOfLoss
    ? dayjs(data.matterDateOfLoss).format("MM/DD/YY")
    : "";

  if (contactName && dateOfLoss) {
    return `${contactName} - ${dateOfLoss}`;
  }

  return contactName || untitledFallback;
};

export function LeadEditAside({
  isOpen,
  selectedLead,
  matterStatuses,
  matterStatusGroups,
  conversionMatterStatusGroups,
  conversionTarget,
  matterRoles,
  matterTeams,
  incidentTypes,
  canTransitionMatterStatus,
  isMatterSettingsLoading,
  isMatterSettingsError,
  onClose,
  intakePipelineCategory = "lead",
  refreshLeadsListData,
}: LeadEditAsideProps) {
  const { currentUser, hasActionPermission } = useUser();
  const queryClient = useQueryClient();
  const pipelineDisplayLabel =
    MATTER_STATUS_CATEGORY_DISPLAY_LABELS[intakePipelineCategory];
  const pipelineDisplayLower = pipelineDisplayLabel.toLowerCase();
  const untitledMatterTitle = `Untitled ${pipelineDisplayLabel}`;
  const newIntakeAsideTitle = `New ${pipelineDisplayLower}`;
  const selectedLeadMatterId = selectedLead?.id ?? "";
  const [deletingMatterId, setDeletingMatterId] = useState<string | null>(null);
  const isDeletingSelectedMatter = deletingMatterId === selectedLeadMatterId;
  const { data: selectedMatterData, isPending: isSelectedMatterPending } =
    useQuery({
      ...retrieveMatterQueryOptions(selectedLeadMatterId),
      enabled: isOpen && !!selectedLeadMatterId && !isDeletingSelectedMatter,
    });
  const selectedMatter = isDeletingSelectedMatter
    ? undefined
    : selectedMatterData?.data;
  const { data: selectedMatterNotesResponse } = useQuery({
    ...retrieveMatterNotesQueryOptions({
      matterId: selectedMatter?.id ?? "",
      pageSize: 1000,
    }),
    enabled: isOpen && Boolean(selectedMatter?.id),
  });
  const selectedMatterNotes = useMemo(
    () => selectedMatterNotesResponse?.data ?? [],
    [selectedMatterNotesResponse?.data],
  );
  const selectedMatterClientConnection = useMemo(
    () =>
      (selectedMatter
        ? getMatterContactConnections(selectedMatter)
        : undefined
      )?.find((connection) => connection.label === "Client"),
    [selectedMatter],
  );
  const selectedMatterClientContactId =
    selectedMatterClientConnection?.contactId;
  const selectedContact = selectedMatterClientConnection?.contact;
  const isSelectedContactPending = false;
  const selectedLeadClientConnection = selectedLead
    ? getMatterSearchResultClientConnection(selectedLead)
    : undefined;
  const selectedLeadStatusLabel =
    selectedMatter?.status.label || selectedLead?.status?.label || "Active";
  const leadTitle =
    getMatterConnectionContactName(selectedMatterClientConnection) ||
    selectedMatter?.title ||
    getMatterSearchResultClientName(selectedLead ?? {}) ||
    selectedLead?.title ||
    (selectedLead ? untitledMatterTitle : newIntakeAsideTitle);
  const leadType =
    selectedMatter?.incidentType?.label ||
    selectedLead?.incidentType?.label ||
    pipelineDisplayLabel;
  const selectedLeadNameParts = getLeadNameParts(
    getMatterConnectionContactName(selectedMatterClientConnection) ||
      getMatterSearchResultClientName(selectedLead ?? {}) ||
      selectedLead?.title,
  );
  const selectedLeadPhone = getPrimaryPhoneNumber(
    selectedMatterClientConnection?.contact.phones ??
      selectedLeadClientConnection?.contact?.phones,
  );
  const selectedLeadEmail = getPrimaryEmailAddress(
    selectedMatterClientConnection?.contact.emails ??
      selectedLeadClientConnection?.contact?.emails,
  );
  const selectedLeadStatusId = useMemo(() => {
    if (selectedMatter?.statusId) {
      return selectedMatter.statusId;
    }

    if (!selectedLead) {
      return undefined;
    }

    return selectedLead.status?.id ?? selectedLead.statusId;
  }, [selectedLead, selectedMatter?.statusId]);
  const selectedLeadIncidentTypeId =
    selectedMatter?.incidentTypeId ??
    selectedLead?.incidentType?.id ??
    selectedLead?.incidentTypeId ??
    "";
  const defaultLeadStatusId = useMemo(
    () =>
      matterStatuses.find(
        (status) => status.categoryId === intakePipelineCategory,
      )?.id ?? "",
    [matterStatuses, intakePipelineCategory],
  );
  const selectedLeadProjectedSol = useMemo(() => {
    const primaryStatute = selectedMatter
      ? getPrimaryMatterStatute(selectedMatter)
      : undefined;

    if (primaryStatute?.date) {
      return formatFormDateValue(primaryStatute.date);
    }

    const leadPrimaryStatute = resolveReportPrimaryStatute(
      selectedLead?.statutesOfLimitations?.map((statute) => ({
        ...statute,
        date: statute.date,
        description: statute.description ?? undefined,
      })) ?? [],
    );

    return formatFormDateValue(leadPrimaryStatute.matterStatuteOfLimitations);
  }, [selectedLead?.statutesOfLimitations, selectedMatter]);
  const selectedLeadNoteMessage = useMemo(
    () =>
      selectedMatter
        ? formatNoteMessageForForm(
            getLeadIntakeMatterNote(selectedMatterNotes)?.message,
          )
        : "",
    [selectedMatter, selectedMatterNotes],
  );
  const {
    data: claimTypeData,
    isLoading: isClaimTypesLoading,
    isError: isClaimTypesError,
  } = useQuery(retrieveClaimTypesQueryOptions());
  const formValues = useMemo<LeadAsideFormValues>(
    () => ({
      firstName: selectedLeadNameParts.firstName,
      lastName: selectedLeadNameParts.lastName,
      phone: selectedLeadPhone,
      email: selectedLeadEmail,
      individualPrimaryLanguage:
        selectedContact?.individualPrimaryLanguage ?? "",
      individualTimezone: selectedContact?.individualTimezone ?? "",
      individualSmsConsent: selectedContact?.individualSmsConsent ?? false,
      matterStatusId: selectedLeadStatusId ?? defaultLeadStatusId,
      incidentTypeId: selectedLeadIncidentTypeId,
      claimTypeIds:
        selectedMatter?.claimTypeIds ?? selectedLead?.claimTypeIds ?? [],
      matterDateOfLoss:
        selectedMatter?.dateOfLoss ?? selectedLead?.dateOfLoss ?? "",
      matterStatuteOfLimitationsDate: selectedLeadProjectedSol,
      matterIncidentAddressState:
        selectedMatter?.incidentAddressState ??
        selectedLead?.incidentAddressState ??
        "",
      matterIncidentDescription: selectedMatter?.incidentDescription ?? "",
      matterClientInjuryDescription:
        selectedMatter?.clientInjuryDescription ?? "",
      matterClientSoughtTreatment:
        selectedMatter?.clientSoughtTreatment ?? false,
      matterClientMinor: selectedMatter?.clientMinorAtIncident ?? false,
      matterNoteMessage: selectedLeadNoteMessage,
    }),
    [
      selectedLead?.claimTypeIds,
      selectedLead?.dateOfLoss,
      selectedLead?.incidentAddressState,
      selectedLeadEmail,
      selectedLeadNoteMessage,
      selectedLeadProjectedSol,
      selectedLeadStatusId,
      selectedLeadIncidentTypeId,
      selectedLeadNameParts.firstName,
      selectedLeadNameParts.lastName,
      selectedLeadPhone,
      selectedContact?.individualPrimaryLanguage,
      selectedContact?.individualTimezone,
      selectedContact?.individualSmsConsent,
      defaultLeadStatusId,
      selectedMatter?.incidentDescription,
      selectedMatter?.clientInjuryDescription,
      selectedMatter?.clientSoughtTreatment,
      selectedMatter?.clientMinorAtIncident,
      selectedMatter?.claimTypeIds,
      selectedMatter?.dateOfLoss,
      selectedMatter?.incidentAddressState,
    ],
  );
  const form = useForm<LeadAsideFormValues>({
    resolver: zodResolver(leadAsideFormSchema),
    values: formValues,
    mode: "onSubmit",
  });
  const createContact = useCreateContact(SILENT_COMPOSED_MUTATION_OPTIONS);
  const updateContact = useUpdateContact(SILENT_COMPOSED_MUTATION_OPTIONS);
  const createContactEmail = useCreateContactEmail(
    SILENT_COMPOSED_MUTATION_OPTIONS,
  );
  const updateContactEmail = useUpdateContactEmail(
    SILENT_COMPOSED_MUTATION_OPTIONS,
  );
  const createContactPhone = useCreateContactPhone(
    SILENT_COMPOSED_MUTATION_OPTIONS,
  );
  const updateContactPhone = useUpdateContactPhone(
    SILENT_COMPOSED_MUTATION_OPTIONS,
  );
  const createMatter = useCreateMatter({
    invalidateOnSuccess: false,
    showSuccessToast: false,
  });
  const updateMatter = useUpdateMatter({
    invalidateOnSettled: false,
    successToast: null,
  });
  const deleteMatter = useDeleteMatter();
  const createMatterAssignee = useCreateMatterAssignee();
  const createMatterTeamConnection = useCreateMatterTeamConnection();
  const createMatterTeammate = useCreateMatterTeammate();
  const createMatterPhase = useCreateMatterStatusPhase(
    SILENT_COMPOSED_MUTATION_OPTIONS,
  );
  const createMatterStatute = useCreateMatterStatuteOfLimitations(
    SILENT_COMPOSED_MUTATION_OPTIONS,
  );
  const updateMatterStatute = useUpdateMatterStatuteOfLimitations(
    SILENT_COMPOSED_MUTATION_OPTIONS,
  );
  const deleteMatterStatute = useDeleteMatterStatuteOfLimitations(
    SILENT_COMPOSED_MUTATION_OPTIONS,
  );
  const createMatterNote = useCreateMatterNote(
    SILENT_COMPOSED_MUTATION_OPTIONS,
  );
  const updateMatterNote = useUpdateMatterNote(
    SILENT_COMPOSED_MUTATION_OPTIONS,
  );
  const deleteMatterNote = useDeleteMatterNote(
    SILENT_COMPOSED_MUTATION_OPTIONS,
  );
  const formInstanceKey = selectedLead?.id ?? `new-${intakePipelineCategory}`;
  const dateOfLossFieldKey = `${formInstanceKey}-date-of-loss-${formValues.matterDateOfLoss}`;
  const projectedSolFieldKey = `${formInstanceKey}-projected-sol-${formValues.matterStatuteOfLimitationsDate}`;
  const [createStatusDropdownOpen, setCreateStatusDropdownOpen] =
    useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isClaimDefaultsOpen, setIsClaimDefaultsOpen] = useState(false);
  const [isConvertToMatterOpen, setIsConvertToMatterOpen] = useState(false);
  const [possibleDuplicateContacts, setPossibleDuplicateContacts] = useState<
    PossibleDuplicateContact[]
  >([]);
  const [pendingLeadCreateData, setPendingLeadCreateData] =
    useState<LeadAsideFormValues | null>(null);
  const [claimDefaultMethod, setClaimDefaultMethod] = useState<"role" | "team">(
    "role",
  );
  const [claimDefaultId, setClaimDefaultId] = useState("");
  const updateUser = useUpdateUser();
  const isSaving =
    createContact.isPending ||
    updateContact.isPending ||
    createContactEmail.isPending ||
    updateContactEmail.isPending ||
    createContactPhone.isPending ||
    updateContactPhone.isPending ||
    createMatter.isPending ||
    updateMatter.isPending ||
    createMatterPhase.isPending ||
    createMatterStatute.isPending ||
    updateMatterStatute.isPending ||
    deleteMatterStatute.isPending ||
    createMatterNote.isPending ||
    updateMatterNote.isPending ||
    deleteMatterNote.isPending;
  const isDeletePending = deleteMatter.isPending;
  const isClaimPending =
    createMatterAssignee.isPending ||
    createMatterTeamConnection.isPending ||
    createMatterTeammate.isPending;
  const canCreateMatterAssignee = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterAssignee,
    ACTION_PERMISSION_VALUES.create,
  );
  const canCreateMatterTeamConnection = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterTeamConnection,
    ACTION_PERMISSION_VALUES.create,
  );
  const canCreateMatterTeammate = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterTeammate,
    ACTION_PERMISSION_VALUES.create,
  );
  const canClaimMatter =
    currentUser.defaultIntakeMethod === "team"
      ? canCreateMatterTeamConnection && canCreateMatterTeammate
      : canCreateMatterAssignee;
  const defaultMatterRole = matterRoles.at(0);
  const matterRolesSorted = useMemo(
    () => [...matterRoles].sort((a, b) => a.label.localeCompare(b.label)),
    [matterRoles],
  );
  const matterTeamsSorted = useMemo(
    () => [...matterTeams].sort((a, b) => a.label.localeCompare(b.label)),
    [matterTeams],
  );
  const matterStatusGroupsForNewLead = useMemo(
    () =>
      matterStatusGroups.filter(
        (group) => group.category === intakePipelineCategory,
      ),
    [matterStatusGroups, intakePipelineCategory],
  );
  const canUpdateClaimDefaults = hasActionPermission(
    ACTION_PERMISSION_KEYS.user,
    ACTION_PERMISSION_VALUES.update,
  );
  const canViewAssignees = true;
  const isCurrentUserAssigned = (selectedMatter?.assignees ?? []).some(
    (assignee) => assignee.user.id === currentUser.id && !assignee.dateTimeEnd,
  );

  const asideAssigneeDisplayNames = useMemo(() => {
    if (!selectedLead) {
      return [];
    }
    if (selectedMatter) {
      const fromMatter = selectedMatter.assignees
        .filter((a) => !a.dateTimeEnd)
        .map((a) => {
          const formatted = formatUsersName(a.user)?.trim();
          if (formatted) {
            return formatted;
          }
          return [a.user.firstName, a.user.lastName]
            .map((part) => part.trim())
            .filter(Boolean)
            .join(" ");
        })
        .filter(Boolean);
      return fromMatter.length > 0
        ? fromMatter
        : getMatterSearchResultAssigneeDisplayNames(selectedLead);
    }
    return getMatterSearchResultAssigneeDisplayNames(selectedLead);
  }, [selectedLead, selectedMatter]);

  const showAssigneesInsteadOfClaim =
    asideAssigneeDisplayNames.length > 0 && canViewAssignees;
  const needsMatterDetail =
    isOpen &&
    !!selectedLead &&
    !!selectedLeadMatterId &&
    !isDeletingSelectedMatter;
  const needsContactDetail = isOpen && !!selectedMatterClientContactId;
  const isLeadAsideDataLoading =
    isMatterSettingsLoading ||
    (needsMatterDetail && isSelectedMatterPending) ||
    (needsContactDetail && isSelectedContactPending);

  useEffect(() => {
    if (isOpen) {
      form.reset(formValues);
    }
  }, [form, formValues, isOpen]);

  useEffect(() => {
    if (!isClaimDefaultsOpen) {
      return;
    }
    const method = currentUser.defaultIntakeMethod === "team" ? "team" : "role";
    setClaimDefaultMethod(method);
    setClaimDefaultId(currentUser.defaultIntakeMethodId ?? "");
  }, [
    isClaimDefaultsOpen,
    currentUser.id,
    currentUser.dateModified,
    currentUser.defaultIntakeMethod,
    currentUser.defaultIntakeMethodId,
  ]);

  const invalidateLeadSearch = () => {
    refreshLeadsListData();
  };

  const createLead = async (
    data: LeadAsideFormValues,
    createDespitePossibleDuplicate = false,
  ) => {
    const matterStatusId = data.matterStatusId;

    const createdContact = await createContact.mutateAsync({
      type: "individual",
      individualFirstName: trimFormValue(data.firstName),
      individualLastName: trimFormValue(data.lastName),
      individualSmsConsent: data.individualSmsConsent,
      createDespitePossibleDuplicate,
      ...(trimFormValue(data.email)
        ? {
            emails: [
              {
                address: trimFormValue(data.email),
                description: "",
                isPrimary: true,
                label: "Primary",
              },
            ],
          }
        : {}),
      ...(trimFormValue(data.individualPrimaryLanguage)
        ? {
            individualPrimaryLanguage: trimFormValue(
              data.individualPrimaryLanguage,
            ) as Contact["individualPrimaryLanguage"],
          }
        : {}),
      ...(trimFormValue(data.individualTimezone)
        ? {
            individualTimezone: trimFormValue(
              data.individualTimezone,
            ) as Contact["individualTimezone"],
          }
        : {}),
      ...(trimFormValue(data.phone)
        ? {
            phones: [
              {
                description: "",
                extension: "",
                isPrimary: true,
                label: "Primary",
                number: trimFormValue(data.phone),
              },
            ],
          }
        : {}),
    });
    if (isPossibleDuplicateResponse(createdContact)) {
      setPossibleDuplicateContacts(createdContact.data);
      setPendingLeadCreateData(data);
      return;
    }
    const contactForConnection: Contact = {
      ...createdContact.data,
    };
    const createdMatter = await createMatter.mutateAsync({
      attorneyFee: 33.33,
      clientInjuryDescription:
        trimFormValue(data.matterClientInjuryDescription) || undefined,
      clientMinorAtIncident: data.matterClientMinor,
      clientSoughtTreatment: data.matterClientSoughtTreatment,
      connections: [
        createContactConnectionChildInput(
          contactForConnection.id,
          MATTER_PARTY_CONNECTION_TYPES.Client,
        ),
        createContactConnectionChildInput(
          contactForConnection.id,
          MATTER_PARTY_CONNECTION_TYPES["Primary Contact"],
        ),
      ],
      dateOfLoss: data.matterDateOfLoss,
      incidentAddressCountry: "United States",
      incidentAddressState: data.matterIncidentAddressState,
      incidentDescription:
        trimFormValue(data.matterIncidentDescription) || undefined,
      statusId: matterStatusId,
      title: buildLeadMatterTitle(data, untitledMatterTitle),
      incidentTypeId: data.incidentTypeId,
      claimTypeIds: data.claimTypeIds,
    });
    const createdMatterId = createdMatter.data.id;
    await syncLeadProjectedStatute({
      matterId: createdMatterId,
      projectedSolDate: data.matterStatuteOfLimitationsDate,
      createMatterStatute,
      updateMatterStatute,
      deleteMatterStatute,
    });
    await syncLeadMatterNote({
      matterId: createdMatterId,
      noteMessage: data.matterNoteMessage,
      currentUser,
      createMatterNote,
      updateMatterNote,
      deleteMatterNote,
    });
    invalidateLeadSearch();
    toast.success(`Successfully created ${pipelineDisplayLower}`);
    onClose();
  };

  const updateLead = async (
    data: LeadAsideFormValues,
    successMessage = `Successfully updated ${pipelineDisplayLower}`,
  ) => {
    if (!selectedMatter) {
      toast.error(
        `Unable to update ${pipelineDisplayLower} before the matter finishes loading`,
      );
      return;
    }

    if (!selectedMatterClientConnection) {
      toast.error("Unable to find the client contact connection");
      return;
    }

    const contact =
      selectedContact ??
      (
        await queryClient.fetchQuery(
          retrieveContactQueryOptions(selectedMatterClientConnection.contactId),
        )
      ).data;

    const individualFirstName = trimFormValue(data.firstName);
    const individualLastName = trimFormValue(data.lastName);
    const individualPrimaryLanguage = trimFormValue(
      data.individualPrimaryLanguage,
    ) as Contact["individualPrimaryLanguage"];
    const individualTimezone = trimFormValue(
      data.individualTimezone,
    ) as Contact["individualTimezone"];
    const shouldUpdateContact =
      individualFirstName !== (contact.individualFirstName ?? "") ||
      individualLastName !== (contact.individualLastName ?? "") ||
      data.individualSmsConsent !== Boolean(contact.individualSmsConsent) ||
      individualPrimaryLanguage !== (contact.individualPrimaryLanguage ?? "") ||
      individualTimezone !== (contact.individualTimezone ?? "");

    if (shouldUpdateContact) {
      await updateContact.mutateAsync({
        id: contact.id,
        dateModified: contact.dateModified,
        individualFirstName,
        individualLastName,
        individualSmsConsent: data.individualSmsConsent,
        individualPrimaryLanguage: individualPrimaryLanguage || undefined,
        individualTimezone: individualTimezone || undefined,
      });
    }

    const primaryEmail =
      contact.emails.find((email) => email.isPrimary) ?? contact.emails.at(0);
    const primaryPhone =
      contact.phones.find((phone) => phone.isPrimary) ?? contact.phones.at(0);
    const email = trimFormValue(data.email);
    const phone = trimFormValue(data.phone);

    await Promise.all([
      email
        ? primaryEmail
          ? email !== primaryEmail.address
            ? updateContactEmail.mutateAsync({
                contactId: contact.id,
                address: email,
                id: primaryEmail.id,
                isPrimary: true,
                label: primaryEmail.label,
                dateModified: primaryEmail.dateModified,
              })
            : Promise.resolve(undefined)
          : createContactEmail.mutateAsync({
              contactId: contact.id,
              address: email,
              isPrimary: true,
              label: "Primary",
            })
        : Promise.resolve(undefined),
      phone
        ? primaryPhone
          ? phone !== primaryPhone.number
            ? updateContactPhone.mutateAsync({
                contactId: contact.id,
                id: primaryPhone.id,
                isPrimary: true,
                label: primaryPhone.label,
                number: phone,
                dateModified: primaryPhone.dateModified,
              })
            : Promise.resolve(undefined)
          : createContactPhone.mutateAsync({
              contactId: contact.id,
              number: phone,
              isPrimary: true,
              label: "Primary",
            })
        : Promise.resolve(undefined),
    ]);

    const title = buildLeadMatterTitle(data, untitledMatterTitle);
    const incidentAddressCountry =
      selectedMatter.incidentAddressCountry || "United States";
    const incidentDescription =
      trimFormValue(data.matterIncidentDescription) || undefined;
    const clientInjuryDescription =
      trimFormValue(data.matterClientInjuryDescription) || undefined;
    const shouldUpdateMatter =
      title !== selectedMatter.title ||
      data.matterDateOfLoss !== (selectedMatter.dateOfLoss ?? "") ||
      incidentAddressCountry !== selectedMatter.incidentAddressCountry ||
      data.matterIncidentAddressState !==
        (selectedMatter.incidentAddressState ?? "") ||
      incidentDescription !==
        (selectedMatter.incidentDescription || undefined) ||
      clientInjuryDescription !==
        (selectedMatter.clientInjuryDescription || undefined) ||
      data.matterClientSoughtTreatment !==
        Boolean(selectedMatter.clientSoughtTreatment) ||
      data.matterClientMinor !==
        Boolean(selectedMatter.clientMinorAtIncident) ||
      data.incidentTypeId !== selectedMatter.incidentTypeId ||
      [...data.claimTypeIds].sort().join() !==
        [...selectedMatter.claimTypeIds].sort().join();

    if (shouldUpdateMatter) {
      await updateMatter.mutateAsync({
        id: selectedMatter.id,
        dateModified: selectedMatter.dateModified,
        title,
        dateOfLoss: data.matterDateOfLoss,
        incidentAddressCountry,
        incidentAddressState: data.matterIncidentAddressState,
        incidentDescription,
        clientInjuryDescription,
        clientSoughtTreatment: data.matterClientSoughtTreatment,
        clientMinorAtIncident: data.matterClientMinor,
        incidentTypeId: data.incidentTypeId,
        claimTypeIds: data.claimTypeIds,
      });
    }
    await syncLeadProjectedStatute({
      matterId: selectedMatter.id,
      projectedSolDate: data.matterStatuteOfLimitationsDate,
      existingStatute: getPrimaryMatterStatute(selectedMatter),
      createMatterStatute,
      updateMatterStatute,
      deleteMatterStatute,
    });
    await syncLeadMatterNote({
      matterId: selectedMatter.id,
      noteMessage: data.matterNoteMessage,
      existingNote: getLeadIntakeMatterNote(selectedMatterNotes),
      currentUser,
      createMatterNote,
      updateMatterNote,
      deleteMatterNote,
    });
    if (data.matterStatusId && data.matterStatusId !== selectedLeadStatusId) {
      await createMatterPhase.mutateAsync({
        dateTimeStart: new Date().toISOString(),
        matterId: selectedMatter.id,
        statusId: data.matterStatusId,
      });
    }
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: [queryKeys.contact, contact.id],
        exact: true,
      }),
      queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, selectedMatter.id],
        exact: true,
      }),
    ]);
    invalidateLeadSearch();
    toast.success(successMessage);
    onClose();
  };

  const onSubmit = async (data: LeadAsideFormValues) => {
    if (selectedLead) {
      await updateLead(data);
      return;
    }

    await createLead(data);
  };

  const handleConversion = (statusId: string) =>
    form.handleSubmit(async (data) => {
      await updateLead(
        { ...data, matterStatusId: statusId },
        `Successfully converted to ${conversionTarget}`,
      );
      setIsConvertToMatterOpen(false);
    })();

  const handleClaimLead = async () => {
    if (!canClaimMatter) {
      toast.error(
        `You do not have permission to claim this ${pipelineDisplayLower}`,
      );
      return;
    }
    if (!selectedMatter) {
      toast.error(
        `Unable to claim ${pipelineDisplayLower} before the matter finishes loading`,
      );
      return;
    }

    if (isCurrentUserAssigned) {
      toast.error(`You are already assigned to this ${pipelineDisplayLower}`);
      return;
    }

    const intakeMethod =
      currentUser.defaultIntakeMethod === "team" ? "team" : "role";
    const intakeMethodId = (currentUser.defaultIntakeMethodId ?? "").trim();

    if (intakeMethod === "role") {
      const role = matterRoles.find((r) => r.id === intakeMethodId);
      if (!role) {
        toast.error(
          "Choose a default matter role in Claim defaults before claiming.",
        );
        return;
      }

      await createMatterAssignee.mutateAsync({
        dateTimeStart: new Date().toISOString(),
        matterId: selectedMatter.id,
        roleId: role.id,
        userId: currentUser.id,
      });
      invalidateLeadSearch();
      return;
    }

    const team = matterTeams.find((t) => t.id === intakeMethodId);
    if (!team) {
      toast.error(
        "Choose a default matter team in Claim defaults before claiming.",
      );
      return;
    }

    if (!defaultMatterRole) {
      toast.error(
        "Your firm must have at least one matter role configured to claim onto a team.",
      );
      return;
    }

    const isAlreadyTeammate = selectedMatter.teamConnections.some((conn) => {
      const connection = conn as MatterTeamConnectionV2;
      if (connection.label !== team.label) {
        return false;
      }
      if (connection.dateTimeEnd) {
        return false;
      }
      return (connection.teammates ?? []).some(
        (tm) => tm.userId === currentUser.id,
      );
    });

    if (isAlreadyTeammate) {
      toast.error(
        `You are already on this team for this ${pipelineDisplayLower}`,
      );
      return;
    }

    const hasActiveTeamConnection = selectedMatter.teamConnections.some(
      (conn) =>
        conn.label === team.label &&
        !(conn as MatterTeamConnectionV2).dateTimeEnd,
    );

    if (!hasActiveTeamConnection) {
      await createMatterTeamConnection.mutateAsync({
        matterId: selectedMatter.id,
        teamId: team.id,
      });
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, selectedMatter.id],
      });
    }

    await createMatterTeammate.mutateAsync({
      teamId: team.id,
      roleId: defaultMatterRole.id,
      user: { userId: currentUser.id },
    });
    await queryClient.invalidateQueries({
      queryKey: [queryKeys.matters, selectedMatter.id],
    });
    invalidateLeadSearch();
  };

  const handleAssignLead = async ({
    user,
    id,
  }: {
    user: DirectoryUser;
    id: string;
  }) => {
    if (!selectedMatter) {
      toast.error(
        `Unable to assign ${pipelineDisplayLower} before the matter finishes loading`,
      );
      return;
    }

    await createMatterAssignee.mutateAsync({
      dateTimeStart: new Date().toISOString(),
      matterId: selectedMatter.id,
      roleId: id,
      userId: user.id,
    });
    invalidateLeadSearch();
    setIsAssigning(false);
  };

  const handleSaveClaimDefaults = async () => {
    if (!canUpdateClaimDefaults) {
      return;
    }

    await updateUser.mutateAsync({
      id: currentUser.id,
      dateModified: currentUser.dateModified,
      defaultIntakeMethod: claimDefaultMethod,
      defaultIntakeMethodId: claimDefaultId.trim() || undefined,
    });
    setIsClaimDefaultsOpen(false);
  };

  const handleDeleteLead = async () => {
    const matterId = selectedMatter?.id ?? selectedLeadMatterId;

    if (!matterId) {
      toast.error(
        `Unable to delete ${pipelineDisplayLower} without a matter ID`,
      );
      return;
    }

    setDeletingMatterId(matterId);
    try {
      await deleteMatter.mutateAsync({ id: matterId });
      setIsDeleting(false);
      onClose();
      invalidateLeadSearch();
    } catch {
      setDeletingMatterId(null);
    }
  };

  return (
    <>
      <aside
        className={cn(
          "bg-card min-w-0 overflow-hidden border-l transition-[flex-basis,opacity] duration-300 ease-in-out",
          isOpen
            ? "basis-full opacity-100 md:basis-1/2"
            : "pointer-events-none basis-0 opacity-0",
        )}
      >
        {isOpen && isLeadAsideDataLoading && (
          <div className="flex h-full min-h-0 min-w-[360px] flex-col">
            <div className="flex shrink-0 items-center justify-end border-b p-4">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onClose}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <LawWorksLoader
              className="min-h-0 flex-1"
              logoClassName="w-[200px] max-w-[70vw]"
            />
          </div>
        )}
        {isOpen && !isLeadAsideDataLoading && (
          <Form {...form} key={formInstanceKey}>
            <form
              key={formInstanceKey}
              className="flex h-full min-h-0 min-w-[360px] flex-col"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <div className="flex shrink-0 items-center justify-between gap-3 border-b p-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Typography weight="semibold" className="truncate">
                      {leadTitle}
                    </Typography>
                    <IncidentTypeBadge
                      variant={getIncidentTypeBadgeVariantFromId(leadType)}
                      className="px-2 py-0 text-[11px]"
                    >
                      {leadType}
                    </IncidentTypeBadge>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {selectedLead && (
                <>
                  <div className="flex shrink-0 items-center justify-between gap-3 border-b p-4">
                    {selectedLead.id ? (
                      <fieldset
                        disabled={!canTransitionMatterStatus || isSaving}
                      >
                        <MultiCategorySelectFormField
                          placeholder={selectedLeadStatusLabel}
                          searchPlaceholder="Search statuses..."
                          emptyMessage="No status found."
                          register={form.register("matterStatusId")}
                          items={matterStatusGroups}
                          open={createStatusDropdownOpen}
                          setOpen={setCreateStatusDropdownOpen}
                          isItemsError={isMatterSettingsError}
                          isItemsLoading={isMatterSettingsLoading}
                          onChange={() => {
                            setCreateStatusDropdownOpen(false);
                          }}
                          buttonClassName="h-auto min-h-0 w-auto rounded-full border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 shadow-none hover:bg-blue-100 hover:text-blue-800"
                        />
                      </fieldset>
                    ) : (
                      <Badge
                        variant="outline"
                        className="border-blue-200 bg-blue-50 text-blue-700"
                      >
                        {selectedLeadStatusLabel}
                      </Badge>
                    )}
                    <Typography className="text-muted-foreground text-sm">
                      Incident:{" "}
                      {formatDate(selectedLead.dateCreated) ?? "No date"}
                    </Typography>
                  </div>

                  <div className="flex shrink-0 flex-wrap items-center gap-3 border-b p-4">
                    {showAssigneesInsteadOfClaim ? (
                      <LeadsAssigneeSummary names={asideAssigneeDisplayNames} />
                    ) : (
                      <div className="flex">
                        <Button
                          type="button"
                          className="rounded-r-none border-r border-neutral-700 bg-neutral-900"
                          disabled={
                            !selectedMatter ||
                            !canClaimMatter ||
                            isClaimPending ||
                            !!isCurrentUserAssigned
                          }
                          onClick={() => {
                            void handleClaimLead();
                          }}
                        >
                          {isClaimPending ? "Claiming..." : "Claim"}
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              className="rounded-l-none bg-neutral-900 px-2"
                              disabled={!selectedMatter}
                              title="Claim options"
                            >
                              <ChevronDown className="h-4 w-4" />
                              <span className="sr-only">Claim options</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                setIsClaimDefaultsOpen(true);
                              }}
                            >
                              <Settings2 className="mr-2 h-4 w-4" />
                              Defaults
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                    <LeadAssignPopover
                      open={isAssigning}
                      setOpen={setIsAssigning}
                      disabled={!selectedMatter || !canCreateMatterAssignee}
                      matterRoles={matterRoles}
                      hasAssignees={asideAssigneeDisplayNames.length > 0}
                      isAssigning={createMatterAssignee.isPending}
                      onAssign={(payload) => {
                        void handleAssignLead(payload);
                      }}
                    />
                  </div>
                </>
              )}
              {!selectedLead && (
                <div className="flex shrink-0 items-center justify-between gap-3 border-b p-4">
                  <MultiCategorySelectFormField
                    label="Status*"
                    placeholder="Select status"
                    searchPlaceholder="Search statuses..."
                    emptyMessage="No status found."
                    register={form.register("matterStatusId")}
                    items={matterStatusGroupsForNewLead}
                    open={createStatusDropdownOpen}
                    setOpen={setCreateStatusDropdownOpen}
                    isItemsError={isMatterSettingsError}
                    isItemsLoading={isMatterSettingsLoading}
                    onChange={() => {
                      setCreateStatusDropdownOpen(false);
                    }}
                    buttonClassName="h-auto min-h-0 w-auto rounded-full border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 shadow-none hover:bg-blue-100 hover:text-blue-800"
                  />
                </div>
              )}

              <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-4">
                <section className="space-y-3">
                  <Typography
                    weight="semibold"
                    className="text-muted-foreground text-xs tracking-wide uppercase"
                  >
                    Contact
                  </Typography>
                  <div className="grid gap-4 md:grid-cols-2">
                    <TextFormField
                      label="First name*"
                      register={form.register("firstName")}
                      placeholder="First name"
                    />
                    <TextFormField
                      label="Last name*"
                      register={form.register("lastName")}
                      placeholder="Last name"
                    />
                    <TextFormField
                      label="Phone"
                      register={form.register("phone")}
                      placeholder="Phone"
                    />
                    <TextFormField
                      label="Email"
                      type="email"
                      register={form.register("email")}
                      placeholder="Email"
                    />
                    <SimpleSelectFormField
                      label="Preferred language"
                      register={form.register("individualPrimaryLanguage")}
                      items={LANGUAGES}
                      placeholder="Select a language"
                      clearable
                    />
                    <SimpleSelectFormField
                      label="Timezone"
                      register={form.register("individualTimezone")}
                      items={CONTACT_INDIVIDUAL_TIMEZONES}
                      formatItemLabel={formatContactIndividualTimezoneLabel}
                      placeholder="Select a timezone"
                      clearable
                    />
                  </div>
                </section>

                <section className="space-y-3">
                  <Typography
                    weight="semibold"
                    className="text-muted-foreground text-xs tracking-wide uppercase"
                  >
                    Case details
                  </Typography>
                  <div className="grid gap-4 md:grid-cols-2">
                    <SelectFormField<{ id: string; label: string }>
                      isItemsLoading={isMatterSettingsLoading}
                      isItemsError={isMatterSettingsError}
                      label="Incident type*"
                      placeholder="Select a type"
                      register={form.register("incidentTypeId")}
                      items={incidentTypes.map((type) => ({
                        ...type,
                        label: getIncidentTypeLabel(type),
                      }))}
                      itemsKey="id"
                      formatItemLabel={(item) => item?.label}
                    />
                    <MultiselectFormField
                      label="Claim types"
                      register={form.register("claimTypeIds")}
                      options={(claimTypeData?.data ?? []).map((claimType) => ({
                        value: claimType.id,
                        label: claimType.label,
                      }))}
                      disabled={isClaimTypesLoading || isClaimTypesError}
                    />
                    {isClaimTypesError && (
                      <p role="alert" className="text-destructive text-sm">
                        Claim types could not be loaded.
                      </p>
                    )}
                    <DateFormField
                      key={dateOfLossFieldKey}
                      register={form.register("matterDateOfLoss")}
                      label="Date of loss*"
                      name="matterDateOfLoss"
                    />
                    <DateFormField
                      key={projectedSolFieldKey}
                      register={form.register("matterStatuteOfLimitationsDate")}
                      label="Projected SOL"
                      name="matterStatuteOfLimitationsDate"
                    />
                    <StateSelectFormField
                      register={form.register("matterIncidentAddressState")}
                      label="Incident state*"
                      name="matterIncidentAddressState"
                      country="United States"
                    />
                    <TextAreaFormField
                      register={form.register("matterIncidentDescription")}
                      label="Incident summary"
                      className="md:col-span-2"
                      textAreaClassName="field-sizing-content max-h-[20lh]"
                    />
                    <TextAreaFormField
                      register={form.register("matterClientInjuryDescription")}
                      label="Injury description"
                      className="md:col-span-2"
                      textAreaClassName="field-sizing-content max-h-[20lh]"
                    />
                    <div className="md:col-span-2">
                      <SwitchFormField
                        register={form.register("matterClientSoughtTreatment")}
                        label={
                          <Typography tag="span" variant="default">
                            Client sought treatment
                          </Typography>
                        }
                      />
                    </div>
                  </div>
                </section>

                <section className="space-y-3">
                  <Typography
                    weight="semibold"
                    className="text-muted-foreground text-xs tracking-wide uppercase"
                  >
                    Notes
                  </Typography>
                  <Textarea
                    {...form.register("matterNoteMessage")}
                    className="bg-background field-sizing-content max-h-[20lh]"
                  />
                </section>

                <section className="space-y-3">
                  <Typography
                    weight="semibold"
                    className="text-muted-foreground text-xs tracking-wide uppercase"
                  >
                    Additional details
                  </Typography>
                  <div className="grid gap-4 md:grid-cols-2">
                    <SwitchFormField
                      register={form.register("matterClientMinor")}
                      label={
                        <Typography tag="span" variant="default">
                          Client is a minor (under 18)
                        </Typography>
                      }
                    />
                    <SwitchFormField
                      register={form.register("individualSmsConsent")}
                      label={
                        <Typography tag="span" variant="default">
                          SMS consent
                        </Typography>
                      }
                    />
                  </div>
                </section>
              </div>

              <div className="flex shrink-0 items-center justify-between border-t p-4">
                {selectedLead ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    onClick={() => {
                      setIsDeleting(true);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </Button>
                ) : (
                  <div />
                )}
                <div className="flex items-center gap-2">
                  <Button type="button" variant="ghost" onClick={onClose}>
                    Cancel
                  </Button>
                  {selectedLead ? (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={
                        !selectedMatter ||
                        !canTransitionMatterStatus ||
                        conversionMatterStatusGroups.length === 0 ||
                        isSaving
                      }
                      onClick={() => {
                        setIsConvertToMatterOpen(true);
                      }}
                    >
                      Convert to {conversionTarget}
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  ) : null}
                  <Button
                    type="submit"
                    className="bg-neutral-900"
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving..." : "Save"}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        )}
      </aside>
      <LeadConversionModal
        open={isConvertToMatterOpen}
        onOpenChange={setIsConvertToMatterOpen}
        currentEntityLabel={pipelineDisplayLower}
        statusGroups={conversionMatterStatusGroups}
        target={conversionTarget}
        isPending={isSaving}
        onConfirm={handleConversion}
      />
      <AppModal
        open={isClaimDefaultsOpen}
        onOpenChange={setIsClaimDefaultsOpen}
        title="Claim defaults"
        description={`Choose whether claiming ${intakePipelineCategory === "opportunity" ? "an" : "a"} ${pipelineDisplayLower} assigns you by matter role or matter team. These are saved to your user profile.`}
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setIsClaimDefaultsOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-neutral-900"
              disabled={!canUpdateClaimDefaults || updateUser.isPending}
              onClick={() => {
                void handleSaveClaimDefaults();
              }}
            >
              {updateUser.isPending ? "Saving…" : "Save defaults"}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {!canUpdateClaimDefaults ? (
            <Typography className="text-muted-foreground text-sm">
              You do not have permission to update user settings. Contact an
              admin to change claim defaults.
            </Typography>
          ) : null}
          <div className="space-y-2">
            <Label
              className="text-foreground text-sm font-medium"
              htmlFor="claim-default-intake-method"
            >
              Intake method
            </Label>
            <Select
              value={claimDefaultMethod}
              onValueChange={(next) => {
                setClaimDefaultMethod(next as "role" | "team");
                setClaimDefaultId("");
              }}
              disabled={!canUpdateClaimDefaults}
            >
              <SelectTrigger
                id="claim-default-intake-method"
                className="bg-background h-9"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="role">Role</SelectItem>
                <SelectItem value="team">Team</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {claimDefaultMethod === "role" ? (
            <div className="space-y-2">
              <Label
                className="text-foreground text-sm font-medium"
                htmlFor="claim-default-matter-role"
              >
                Matter role
              </Label>
              <Select
                value={
                  matterRolesSorted.some((r) => r.id === claimDefaultId)
                    ? claimDefaultId
                    : undefined
                }
                onValueChange={setClaimDefaultId}
                disabled={!canUpdateClaimDefaults || isMatterSettingsLoading}
              >
                <SelectTrigger
                  id="claim-default-matter-role"
                  className="bg-background h-9"
                >
                  <SelectValue
                    placeholder={
                      isMatterSettingsLoading
                        ? "Loading roles…"
                        : "Select a matter role"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {matterRolesSorted.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-2">
              <Label
                className="text-foreground text-sm font-medium"
                htmlFor="claim-default-matter-team"
              >
                Matter team
              </Label>
              <Select
                value={
                  matterTeamsSorted.some((t) => t.id === claimDefaultId)
                    ? claimDefaultId
                    : undefined
                }
                onValueChange={setClaimDefaultId}
                disabled={!canUpdateClaimDefaults || isMatterSettingsLoading}
              >
                <SelectTrigger
                  id="claim-default-matter-team"
                  className="bg-background h-9"
                >
                  <SelectValue
                    placeholder={
                      isMatterSettingsLoading
                        ? "Loading teams…"
                        : "Select a matter team"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {matterTeamsSorted.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      {team.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </AppModal>
      <ActionConfirmationModal
        open={isDeleting && !!selectedLead}
        setOpen={setIsDeleting}
        title={`Are you sure you want to delete this ${pipelineDisplayLower}?`}
        description="This action cannot be undone. This will delete the underlying matter."
        buttonText={`Delete ${pipelineDisplayLower}`}
        afterActionFn={handleDeleteLead}
        isActionPending={isDeletePending}
      />
      <PossibleDuplicateContactModal
        open={possibleDuplicateContacts.length > 0}
        setOpen={(open) => {
          if (!open) {
            setPossibleDuplicateContacts([]);
            setPendingLeadCreateData(null);
          }
        }}
        candidates={possibleDuplicateContacts}
        onCancel={() => {
          setPossibleDuplicateContacts([]);
          setPendingLeadCreateData(null);
        }}
        onCreateAnyway={async () => {
          const data = pendingLeadCreateData;
          if (!data) return;

          setPossibleDuplicateContacts([]);
          setPendingLeadCreateData(null);
          await createLead(data, true);
        }}
      />
    </>
  );
}
